import React, { useEffect, useMemo } from 'react';

import { Container, Pagination } from '@infoods/lifebase-ui';
import { observer } from 'mobx-react';

import classes from './NgzScholasticCustomerList.module.scss';
import { angularizeWithProviders } from '../../../shared/providers';
import { ScholasticCustomerPageTitle } from './ScholasticCustomerPageTitle/ScholasticCustomerPageTitle';
import { TitleRow } from './TitleRow/TitleRow';
import { scholasticCustomersListStore } from '../../stores/ScholasticCustomersListStore';
import { FilterRow } from './FilterRow/FilterRow';
import { CustomerRow } from './CustomerRow/CustomerRow';
import { useCustomFilter } from '../../../shared/features/custom-filter';
import { EmptyList } from '../../../shared/components/ui-components';
import { getVisibleColumns } from '../../../shared/components/table-components';
import { loggedInUserDataStore } from '../../../shared/stores/loggedInUserDataStore';
import {
  COLUMN_WIDTHS,
  SCHOLASTIC_CUSTOMER_COLUMN,
  ShowColumnsConfig,
} from './NgzScholasticCustomerList.settings';

type NgzScholasticCustomerListProps = {
  listTitle: string;
};

const NgzScholasticCustomerList = observer(
  ({ listTitle }: NgzScholasticCustomerListProps) => {
    const { customerList, getCustomerList, filterMethod } = scholasticCustomersListStore;
    const { isShowPagination, isShowPlaceholder, isSearchUsed, getPaginationProps } =
      filterMethod;
    const { isCoach, isSuperAdmin } = loggedInUserDataStore;

    useCustomFilter('scholastic');

    useEffect(() => {
      getCustomerList();
    }, []);

    const paginationProps = getPaginationProps();

    const showColumnsConfig = useMemo<ShowColumnsConfig>(
      () => ({
        [SCHOLASTIC_CUSTOMER_COLUMN.NAME]: true,
        [SCHOLASTIC_CUSTOMER_COLUMN.LOCATION]: !!isSuperAdmin,
        [SCHOLASTIC_CUSTOMER_COLUMN.AFFILIATE]: true,
        [SCHOLASTIC_CUSTOMER_COLUMN.COACH]: !isCoach,
        [SCHOLASTIC_CUSTOMER_COLUMN.START_DATE]: true,
        [SCHOLASTIC_CUSTOMER_COLUMN.STATUS]: true,
        [SCHOLASTIC_CUSTOMER_COLUMN.CUSTOM_FILTER]: true,
        [SCHOLASTIC_CUSTOMER_COLUMN.ACTIONS]: true,
      }),
      [isCoach, isSuperAdmin],
    );

    const { visibleColumns, gridTemplateColumns } = getVisibleColumns({
      columnEnum: SCHOLASTIC_CUSTOMER_COLUMN,
      showColumnsConfig,
      columnWidths: COLUMN_WIDTHS,
    });

    return (
      <Container>
        <ScholasticCustomerPageTitle title={listTitle} />
        <div
          className={classes.scholasticTable}
          style={{ display: 'grid', gridTemplateColumns }}
        >
          <TitleRow visibleColumns={visibleColumns} />
          <FilterRow visibleColumns={visibleColumns} />
          {customerList.map((userInfo) => (
            <CustomerRow
              userInfo={userInfo}
              key={userInfo.id}
              visibleColumns={visibleColumns}
            />
          ))}
        </div>
        {isShowPagination && <Pagination {...paginationProps} />}
        <EmptyList
          show={isShowPlaceholder}
          isSearch={isSearchUsed}
          description="There are no customers"
        />
      </Container>
    );
  },
);

angularizeWithProviders(NgzScholasticCustomerList, 'ngzScholasticCustomerList', {
  listTitle: '<',
});

export { NgzScholasticCustomerList };
